<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import AnalyticsPanel from './AnalyticsPanel.vue';
import MetricValue from './MetricValue.vue';
import { dayLabel, fillDays } from './analyticsHelpers';
import { useAnalyticsChart } from './useAnalyticsChart';
import { useAnalyticsFormat } from './useAnalyticsFormat';

// Só existe na resposta de super admin: o servidor corta o bloco para os demais, e
// a página o monta pela presença da chave. Aqui não há checagem de papel.
const props = defineProps({
  cost: { type: Object, default: null },
  agentNames: { type: Object, default: () => ({}) },
  since: { type: String, default: '' },
  until: { type: String, default: '' },
  runtimeReason: { type: String, default: '' },
});

const { t } = useI18n();
const { number, seconds, usd, orNoData } = useAnalyticsFormat();
const { palette, bar, chartOptions } = useAnalyticsChart();

const perAgent = computed(() => props.cost?.per_agent || []);

const turnTime = latency => {
  const p50 = orNoData(latency?.p50, value => seconds(value / 1000));
  if (typeof latency?.p90 !== 'number') return p50;
  return t('ANALYTICS_REPORTS.COST.TURN_TIME_VALUE', {
    p50,
    p90: seconds(latency.p90 / 1000),
  });
};

const days = computed(() =>
  fillDays(props.cost?.daily || [], props.since, props.until, { usd: 0 })
);
const hasDailyCost = computed(() => days.value.some(row => row.usd > 0));

const collection = computed(() => ({
  labels: days.value.map(row => dayLabel(row.day)),
  datasets: [
    bar(
      t('ANALYTICS_REPORTS.COST.USD'),
      days.value.map(row => row.usd),
      palette.value.ia
    ),
  ],
}));
const options = computed(() => chartOptions(usd, { integer: false }));
</script>

<template>
  <AnalyticsPanel
    :title="t('ANALYTICS_REPORTS.COST.TITLE')"
    :description="t('ANALYTICS_REPORTS.COST.DESCRIPTION')"
    data-test-id="analytics-cost"
  >
    <template #aside>
      <span
        class="px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        {{ t('ANALYTICS_REPORTS.COST.BADGE') }}
      </span>
    </template>

    <MetricValue v-if="!cost" :reason="runtimeReason" />
    <template v-else>
      <dl class="grid gap-4 sm:grid-cols-2">
        <div class="min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.COST.TOTAL') }}
          </dt>
          <dd>
            <MetricValue
              :value="cost.total_usd"
              :format="usd"
              class="text-2xl font-medium tabular-nums text-n-slate-12"
            />
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t('ANALYTICS_REPORTS.COST.SPLIT', {
                  turns: orNoData(cost.turns_usd, usd),
                  followup: orNoData(cost.followup_usd, usd),
                })
              }}
            </p>
          </dd>
        </div>
        <div class="min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{ t('ANALYTICS_REPORTS.COST.TOKENS') }}
          </dt>
          <dd class="text-sm text-n-slate-12">
            <p class="mb-0">
              {{
                t('ANALYTICS_REPORTS.COST.TOKENS_DETAIL', {
                  input: orNoData(cost.tokens_in, number),
                  output: orNoData(cost.tokens_out, number),
                })
              }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t('ANALYTICS_REPORTS.COST.CACHE_DETAIL', {
                  read: orNoData(cost.cache_read, number),
                  write: orNoData(cost.cache_write, number),
                })
              }}
            </p>
          </dd>
        </div>
      </dl>

      <div v-if="perAgent.length" class="flex flex-col gap-2 min-w-0">
        <h3 class="text-xs font-medium text-n-slate-11">
          {{ t('ANALYTICS_REPORTS.COST.PER_AGENT') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="py-2 text-xs font-medium text-start pe-4 text-n-slate-11"
                >
                  {{ t('ANALYTICS_REPORTS.COST.AGENT') }}
                </th>
                <th
                  class="px-3 py-2 text-xs font-medium text-end text-n-slate-11"
                >
                  {{ t('ANALYTICS_REPORTS.COST.USD') }}
                </th>
                <th
                  class="px-3 py-2 text-xs font-medium text-start text-n-slate-11"
                >
                  {{ t('ANALYTICS_REPORTS.COST.MODELS') }}
                </th>
                <th
                  class="px-3 py-2 text-xs font-medium text-end text-n-slate-11"
                >
                  {{ t('ANALYTICS_REPORTS.COST.TURN_TIME') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="item in perAgent" :key="item.slug">
                <td class="py-2.5 font-medium pe-4 text-n-slate-12">
                  <span class="block truncate max-w-[10rem]">
                    {{ agentNames[item.slug] || item.slug }}
                  </span>
                </td>
                <td
                  class="px-3 py-2.5 whitespace-nowrap tabular-nums text-end text-n-slate-12"
                >
                  <MetricValue :value="item.usd" :format="usd" />
                </td>
                <td class="px-3 py-2.5 whitespace-nowrap text-n-slate-11">
                  <span
                    v-for="model in item.models || []"
                    :key="model"
                    class="block"
                  >
                    {{ model }}
                  </span>
                </td>
                <td
                  class="px-3 py-2.5 whitespace-nowrap tabular-nums text-end text-n-slate-12"
                >
                  {{ turnTime(item.turn_latency_ms) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex flex-col gap-2 min-w-0">
        <h3 class="text-xs font-medium text-n-slate-11">
          {{ t('ANALYTICS_REPORTS.COST.DAILY') }}
        </h3>
        <div v-if="hasDailyCost" class="relative h-56">
          <BarChart :collection="collection" :chart-options="options" />
        </div>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          {{ t('ANALYTICS_REPORTS.COST.EMPTY_DAILY') }}
        </p>
      </div>
    </template>
  </AnalyticsPanel>
</template>
